import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ShieldCheck } from "lucide-react";
import { adminClient, apiError } from "@/lib/admin";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mfaStep, setMfaStep] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await adminClient.post("/admin/login", {
        email,
        password,
        ...(code ? { code } : {}),
      });
      if (data.mfa_required) {
        setMfaStep(true);
        setCode("");
        return;
      }
      navigate("/admin");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-[#E8DCC4] rounded-sm px-4 py-3 text-base text-neutral-900 placeholder:text-neutral-500 focus:outline-none focus:border-[#C9A227] transition-colors duration-300";

  return (
    <main className="min-h-screen bg-[#F5F3EC] flex items-center justify-center px-6" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm bg-white border border-[#E8DCC4] rounded-sm p-8">
        <div className="text-center mb-8">
          <Lock size={22} strokeWidth={1.5} className="mx-auto text-[#C9A227]" />
          <h1 className="font-display text-2xl text-neutral-900 mt-3">Gold Time Admin</h1>
          <p className="text-xs text-neutral-500 mt-1 uppercase tracking-[0.2em]">Owner access only</p>
        </div>
        {error && (
          <p className="mb-4 text-sm text-red-400 bg-red-950/40 border border-red-900 rounded-sm px-3 py-2" data-testid="admin-login-error">
            {error}
          </p>
        )}
        {!mfaStep ? (
          <>
            <input
              type="email" required placeholder="Email" value={email} autoComplete="username"
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls} data-testid="admin-login-email"
            />
            <input
              type="password" required placeholder="Password" value={password} autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              className={`${inputCls} mt-3`} data-testid="admin-login-password"
            />
          </>
        ) : (
          <>
            <p className="text-sm text-neutral-600 mb-3 flex items-center gap-2">
              <ShieldCheck size={16} strokeWidth={1.5} className="text-[#C9A227]" />
              Enter the 6-digit code from your authenticator app.
            </p>
            <input
              type="text" required inputMode="numeric" autoComplete="one-time-code"
              placeholder="000000" value={code} maxLength={8}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className={`${inputCls} text-center tracking-[0.5em] text-lg`} data-testid="admin-login-mfa-code"
            />
            <button
              type="button"
              onClick={() => { setMfaStep(false); setCode(""); setError(""); }}
              className="mt-2 text-xs text-neutral-500 hover:text-[#A8871B]"
              data-testid="admin-login-back"
            >
              Back to email &amp; password
            </button>
          </>
        )}
        <button
          type="submit" disabled={busy}
          className="mt-5 w-full rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold py-3 hover:from-[#F0D988] hover:to-[#D4AF37] transition-colors duration-300 disabled:opacity-60"
          data-testid="admin-login-submit"
        >
          {busy ? "Signing in…" : mfaStep ? "Verify & sign in" : "Sign in"}
        </button>
      </form>
    </main>
  );
}
